import { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { ListRenderItem } from '@shopify/flash-list';
import { useBoolean } from 'usehooks-ts';

import { Input } from '../Input';
import { Text } from '../Text';
import { FlashList } from '../FlashList';
import { Divider } from '../Divider';
import { colors, shadows } from '../../theme';
import { Props } from './types';
import { useInput } from '../../hooks';

export const AutocompleteInput = ({
	suggestions,
	onSelect,
	inputProps = {},
	addNewTextResolver,
}: Props) => {
	const { value, ...input } = useInput();

	const { t } = useTranslation();

	const {
		value: suggestionsOpen,
		setTrue: showSuggestions,
		setFalse: closeSuggestions,
	} = useBoolean(false);

	const filteredSuggestions = useMemo(() => {
		if (!value || !suggestions) return [];

		return suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()));
	}, [value, suggestions]);

	const [hoveredIndex, setHoveredIndex] = useState<number>();

	const selectAndClear = useCallback(
		(value: string) => {
			onSelect(value);
			input.reset();
		},
		[onSelect, input],
	);

	const handleSuggestionPress = useCallback(
		(suggestion: string) => () => {
			selectAndClear(suggestion);
			closeSuggestions();
			setHoveredIndex(undefined);
		},
		[closeSuggestions, selectAndClear],
	);

	const onFocusHandler = useCallback(() => {
		showSuggestions();
	}, [showSuggestions]);

	const onBlurHandler = useCallback(() => {
		if (!suggestionsOpen || hoveredIndex !== undefined) {
			return;
		}
		closeSuggestions();
		setHoveredIndex(undefined);
	}, [closeSuggestions, suggestionsOpen, hoveredIndex]);

	const renderItem: ListRenderItem<string> = useCallback(
		({ item, index }) => {
			return (
				<Pressable
					onPress={handleSuggestionPress(item)}
					onHoverIn={() => setHoveredIndex(index)}
					onHoverOut={() => setHoveredIndex(undefined)}
					style={index === hoveredIndex && styles.hovered}
				>
					<Text>{item}</Text>
				</Pressable>
			);
		},
		[handleSuggestionPress, hoveredIndex],
	);

	const onSubmitEditingHandler = useCallback(() => {
		if (!value) {
			return;
		}

		selectAndClear(value);
	}, [selectAndClear, value]);

	const suggestionsVisible = suggestionsOpen && value?.length;

	return (
		<View style={styles.root}>
			<Input
				placeholder={t('autocomplete-input.placeholder')}
				onSubmitEditing={onSubmitEditingHandler}
				blurOnSubmit={false}
				{...inputProps}
				value={value}
				{...input}
				onFocus={onFocusHandler}
				onBlur={onBlurHandler}
			/>

			<FlashList
				style={[styles.suggestionsContainer, !suggestionsVisible && styles.hidden]}
				data={filteredSuggestions}
				renderItem={renderItem}
				ItemSeparatorComponent={Divider}
				ListEmptyComponent={
					<Pressable
						onPress={onSubmitEditingHandler}
						onHoverIn={() => setHoveredIndex(0)}
						onHoverOut={() => setHoveredIndex(undefined)}
						style={0 === hoveredIndex && styles.hovered}
					>
						<Text>
							{addNewTextResolver?.(value) || t('autocomplete-input.add-value', { value })}
						</Text>
					</Pressable>
				}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		zIndex: 1,
	},
	suggestionsContainer: {
		position: 'absolute',
		top: 75,
		left: 0,
		right: 0,
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 10,
		maxHeight: 150,
		zIndex: 1,
		...shadows.drop3,
	},
	hidden: {
		display: 'none',
	},
	hovered: {
		backgroundColor: colors.neutral,
	},
});
